import { motion } from "framer-motion";
import { TrendingUp, Code2, Server, Bot, CalendarCheck2, Search, ArrowUpRight } from "lucide-react";
import { IMAGES } from "@/lib/site";

const SERVICES = [
  {
    id: "service-card-marketing",
    icon: TrendingUp,
    tag: "01 / Growth Engine",
    name: "Performance Marketing",
    desc: "Data-driven online marketing across every channel — paid ads, funnels, brand positioning, and ROI-focused customer acquisition.",
    features: ["Paid Search & Social", "Conversion Rate Optimization", "Analytics Architecture", "Content Strategy"],
    image: IMAGES.marketing,
    span: "lg:col-span-7",
    wide: true,
  },
  {
    id: "service-card-webdev",
    icon: Code2,
    tag: "02 / Digital Architecture",
    name: "Bespoke Web Development",
    desc: "Ultra-fast web applications, headless CMS setups, interactive landing experiences, and API integrations engineered for conversion.",
    features: ["React & Next.js Apps", "Headless E-Commerce", "Custom UI/UX Systems", "Core Web Vitals 100/100"],
    image: IMAGES.webdev,
    span: "lg:col-span-5",
  },
  {
    id: "service-card-chatbots",
    icon: Bot,
    tag: "03 / Always-On Sales",
    name: "AI Chatbots",
    desc: "24/7 AI chatbots that answer, qualify, and convert visitors while you sleep — trained on your business and plugged into your site and socials.",
    features: ["24/7 Lead Capture", "Trained On Your Business", "Website & Social Integration", "Human Handover"],
    image: IMAGES.chatbots,
    span: "lg:col-span-5",
  },
  {
    id: "service-card-booking",
    icon: CalendarCheck2,
    tag: "04 / Full Calendar",
    name: "Booking Systems",
    desc: "Appointment and booking systems that fill your calendar automatically — online scheduling, reminders, deposits, and payments built in.",
    features: ["Online Scheduling", "SMS & Email Reminders", "Deposits & Payments", "Calendar Sync"],
    image: IMAGES.booking,
    span: "lg:col-span-7",
    wide: true,
  },
  {
    id: "service-card-seo",
    icon: Search,
    tag: "05 / Get Found",
    name: "SEO & Search Growth",
    desc: "Get found on Google. Technical SEO, local search, and content that compounds into long-term organic growth and free traffic.",
    features: ["Technical SEO Audits", "Local Search & Maps", "Keyword & Content Strategy", "Monthly Rank Reporting"],
    image: IMAGES.seo,
    span: "lg:col-span-6",
  },
  {
    id: "service-card-hosting",
    icon: Server,
    tag: "06 / Infrastructure",
    name: "Enterprise Hosting Systems",
    desc: "Managed cloud infrastructure with automated failover, NVMe ultra-low latency servers, global CDN deployment, and 24/7 security monitoring.",
    features: ["99.99% Guaranteed SLA", "Automated Daily Backups", "Isolated Cloud NVMe VPS", "DDoS Mitigation & WAF"],
    image: IMAGES.hosting,
    span: "lg:col-span-6",
  },
];

const Card = ({ s, i }) => (
  <motion.article
    data-testid={s.id}
    initial={{ opacity: 0, y: 32 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-80px" }}
    transition={{ duration: 0.6, delay: (i % 2) * 0.12 }}
    className={`md:col-span-8 ${s.span} group relative rounded-3xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 overflow-hidden hover:border-primary/50 hover:shadow-2xl hover:shadow-blue-500/10 transition-all duration-300`}
  >
    <div className={`grid ${s.wide ? "lg:grid-cols-2" : ""}`}>
      <div className="p-6 sm:p-10">
        <p className="font-semibold text-xs uppercase tracking-[0.2em] text-blue-600 dark:text-blue-400">{s.tag}</p>
        <div className="mt-5 flex items-center gap-3">
          <span className="p-3 rounded-2xl bg-primary/10 text-primary"><s.icon className="w-6 h-6" /></span>
          <h3 className="font-bold text-xl sm:text-2xl tracking-tight">{s.name}</h3>
        </div>
        <p className="mt-4 text-sm sm:text-base leading-relaxed text-slate-600 dark:text-slate-300">{s.desc}</p>
        <ul className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2">
          {s.features.map((f) => (
            <li key={f} className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-300">
              <span className="w-1.5 h-1.5 rounded-full bg-primary shrink-0" /> {f}
            </li>
          ))}
        </ul>
        <a href={s.id === "service-card-seo" ? "/seo" : "#contact"} className="mt-8 inline-flex items-center gap-1.5 text-sm font-semibold text-primary group-hover:gap-3 transition-all">
          {s.id === "service-card-seo" ? "See SEO packages" : "Discuss this capability"} <ArrowUpRight className="w-4 h-4" />
        </a>
      </div>
      <div className="relative min-h-52">
        <img src={s.image} alt={s.name} loading="lazy"
             className="absolute inset-0 w-full h-full object-cover opacity-90 group-hover:scale-105 transition-transform duration-700" />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950/40 to-transparent" />
      </div>
    </div>
  </motion.article>
);

export const Services = () => (
  <section id="services" className="py-20 lg:py-32">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
      <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}
                  className="max-w-2xl mb-14">
        <p className="font-semibold text-xs uppercase tracking-[0.2em] text-blue-600 dark:text-blue-400 mb-4">What we do</p>
        <h2 className="font-bold tracking-tight text-2xl sm:text-3xl lg:text-4xl">Integrated Capabilities</h2>
        <p className="mt-4 text-base sm:text-lg text-slate-600 dark:text-slate-300">
          Six engines driving end-to-end digital dominance — one partner, zero hand-offs.
        </p>
      </motion.div>
      <div data-testid="services-bento-grid" className="grid grid-cols-1 md:grid-cols-8 lg:grid-cols-12 gap-6 lg:gap-8">
        {SERVICES.map((s, i) => <Card key={s.id} s={s} i={i} />)}
      </div>
    </div>
  </section>
);
